/**
 * IntakePanel - Left panel for idea input and file upload
 * "Start Your Journey" intake section
 */

import { useState, useCallback } from 'react'
import { Lightbulb, FileText, Sparkles, ArrowRight, Loader2 } from 'lucide-react'
import { Documents3UploaderCompact } from '@/components/document-processing/Documents3UploaderCompact'
import { useSessionStore } from '@/stores/sessionStore'
import { useJourneyStore } from '@/stores/journeyStore'
import type { Document2WithEntities } from '@/lib/api/client'

export function IntakePanel() {
  const [ideaText, setIdeaText] = useState('')
  const [showUploader, setShowUploader] = useState(false)
  const [isStarting, setIsStarting] = useState(false)
  const { accountId, projectId } = useSessionStore()
  const { fetchJourney } = useJourneyStore()

  // Handle document upload completion
  const handleDocumentComplete = useCallback(async (doc: Document2WithEntities) => {
    setShowUploader(false)
    // Refresh journey to pick up new entity counts
    if (accountId && projectId) {
      await fetchJourney(accountId, projectId)
    }
  }, [accountId, projectId, fetchJourney])

  // Handle start discovery
  const handleStartDiscovery = useCallback(async () => {
    if (!ideaText.trim()) return
    setIsStarting(true)
    // TODO: Send idea to discovery coach
    setIsStarting(false)
  }, [ideaText])

  return (
    <div className="p-6 h-full flex flex-col">
      {/* Header */}
      <div className="flex items-start gap-3 mb-6">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center text-white"
          style={{ background: 'linear-gradient(135deg, var(--color-market) 0%, #4a7058 100%)' }}
        >
          <Sparkles className="w-5 h-5" />
        </div>
        <div>
          <h2 className="text-base font-semibold text-text">Start Your Journey</h2>
          <p className="text-xs text-text-muted mt-0.5">
            Describe your idea or upload existing materials
          </p>
        </div>
      </div>

      {/* Idea Input */}
      <div className="mb-5">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-4 h-4 rounded-full border-2 border-market flex items-center justify-center">
            <div className="w-2 h-2 rounded-full bg-market" />
          </div>
          <label className="flex items-center gap-1.5 text-sm font-medium text-text">
            <Lightbulb className="w-3.5 h-3.5 text-warm-500" />
            Enter your idea
          </label>
        </div>
        <textarea
          value={ideaText}
          onChange={(e) => setIdeaText(e.target.value)}
          placeholder="e.g., A platform that helps small restaurants manage their online orders..."
          rows={4}
          className="w-full px-3 py-2.5 border border-border rounded-lg bg-warm-50 text-sm resize-none
                     focus:outline-none focus:border-market focus:bg-white transition-colors"
        />
      </div>

      {/* Divider */}
      <div className="flex items-center gap-3 my-4">
        <div className="flex-1 h-px bg-border-subtle" />
        <span className="text-xs text-text-muted">and/or</span>
        <div className="flex-1 h-px bg-border-subtle" />
      </div>

      {/* File Upload */}
      <div className="mb-5">
        <div className="flex items-center gap-2 mb-3">
          <div className={`w-4 h-4 rounded-full border-2 ${showUploader ? 'border-market' : 'border-border'}`}>
            {showUploader && <div className="w-2 h-2 m-0.5 rounded-full bg-market" />}
          </div>
          <label className="flex items-center gap-1.5 text-sm font-medium text-text">
            <FileText className="w-3.5 h-3.5 text-warm-500" />
            Upload information
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold text-white"
              style={{ background: 'linear-gradient(135deg, var(--color-market) 0%, #4a7058 100%)' }}
            >
              <Sparkles className="w-3 h-3" />
              AI Analysis
            </span>
          </label>
        </div>

        {showUploader ? (
          <div className="space-y-2">
            <Documents3UploaderCompact
              onComplete={handleDocumentComplete}
              onError={() => setShowUploader(false)}
            />
            <button
              onClick={() => setShowUploader(false)}
              className="w-full py-2 text-xs text-warm-500 hover:text-warm-700 transition-colors"
            >
              Cancel upload
            </button>
          </div>
        ) : (
          <button
            onClick={() => setShowUploader(true)}
            className="w-full border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all
                       border-border-subtle bg-warm-50 hover:border-market hover:bg-market/5"
          >
            <FileText className="w-8 h-8 mx-auto mb-2 text-warm-400" />
            <p className="text-sm font-medium text-warm-700">
              Upload pitch deck, business plan, or notes
            </p>
            <span className="text-xs text-text-muted">
              PDF, TXT, MD - AI will extract insights automatically
            </span>
          </button>
        )}
      </div>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Start Button */}
      <button
        onClick={handleStartDiscovery}
        className="w-full py-3.5 rounded-lg text-sm font-semibold text-white flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        style={{ backgroundColor: 'var(--color-market)' }}
        disabled={!ideaText.trim() || isStarting}
      >
        {isStarting ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Starting...
          </>
        ) : (
          <>
            Start Discovery
            <ArrowRight className="w-4 h-4" />
          </>
        )}
      </button>
      <p className="text-center text-xs text-text-muted mt-2">
        Enter an idea or upload a file to continue
      </p>
    </div>
  )
}
